/**
 * TenFrameSVG — Phase 1, Component 1 of the TeachBud Representation Catalog
 *
 * Renders a mathematically accurate ten-frame (or double ten-frame for 11–20)
 * following the Curriculum Rendering Spec §5.1 Counting specification:
 *   - Filled cells: coral (#EF4444)
 *   - Empty cells: light gray (#F3F4F6) with visible border
 *   - Supports student mode (all empty) and teacher/example mode (N filled)
 *   - Grades K–2; cell size scales with the available space
 */

import React from 'react';

export interface TenFrameProps {
  /** How many cells to fill (teacher / example mode). 0–20. */
  value?: number;
  /** When true, all cells are empty — student fills them in. */
  studentMode?: boolean;
  /** Override pixel width. Component scales proportionally. */
  width?: number;
  /** Optional CSS class */
  className?: string;
  /** Show a count label below the frame */
  showLabel?: boolean;
}

// ── Layout constants (for a 10-cell single frame) ────────────────────────────
const COLS = 5;
const ROWS = 2;
const CELL = 52;   // px per cell in the base coordinate system
const GAP  = 8;    // px between cells
const PAD  = 10;   // outer padding

// Single frame dimensions
const FRAME_W = PAD + COLS * CELL + (COLS - 1) * GAP + PAD;  // 356
const FRAME_H = PAD + ROWS * CELL + (ROWS - 1) * GAP + PAD;  // 140
const CIRCLE_R = 20;

// ── Color tokens ─────────────────────────────────────────────────────────────
const FILLED_BG   = '#EF4444';  // coral — filled cell
const FILLED_DOT  = '#FEF2F2';  // very light red dot inside filled cell
const EMPTY_BG    = '#F9FAFB';  // near-white — empty cell
const CELL_STROKE = '#D1D5DB';  // gray-300 border
const FRAME_STROKE= '#9CA3AF';  // gray-400 frame border
const LABEL_COLOR = '#374151';  // gray-700

// ── Helper: render one row of 5 cells ────────────────────────────────────────
function TenFrameRow({
  rowIndex,
  filledCount,
  offsetY,
  studentMode,
}: {
  rowIndex: number;
  filledCount: number;  // how many in this row are filled
  offsetY: number;
  studentMode: boolean;
}) {
  return (
    <>
      {Array.from({ length: COLS }).map((_, col) => {
        const x    = PAD + col * (CELL + GAP);
        const y    = offsetY;
        const cx   = x + CELL / 2;
        const cy   = y + CELL / 2;
        const filled = !studentMode && col < filledCount;

        return (
          <g key={`r${rowIndex}c${col}`}>
            {/* Cell background */}
            <rect
              x={x} y={y}
              width={CELL} height={CELL}
              rx={6} ry={6}
              fill={filled ? FILLED_BG : EMPTY_BG}
              stroke={CELL_STROKE}
              strokeWidth={1.5}
            />
            {/* Circle indicator */}
            <circle
              cx={cx} cy={cy} r={CIRCLE_R}
              fill={filled ? FILLED_DOT : 'none'}
              stroke={filled ? '#FCA5A5' : CELL_STROKE}
              strokeWidth={filled ? 1 : 1.5}
              strokeDasharray={filled ? undefined : '4 3'}
            />
          </g>
        );
      })}
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function TenFrameSVG({
  value = 0,
  studentMode = false,
  width,
  className = '',
  showLabel = true,
}: TenFrameProps) {
  const clampedValue = Math.max(0, Math.min(20, value));
  const isDouble = clampedValue > 10;

  // How many cells per row
  const topFill    = studentMode ? 0 : Math.min(clampedValue, COLS);       // row 0 of frame 1
  const bottomFill = studentMode ? 0 : Math.max(0, Math.min(clampedValue - COLS, COLS)); // row 1 of frame 1
  const topFill2   = studentMode ? 0 : Math.min(Math.max(0, clampedValue - 10), COLS);   // row 0 of frame 2
  const bottomFill2= studentMode ? 0 : Math.max(0, Math.min(clampedValue - 15, COLS));   // row 1 of frame 2

  const LABEL_H = showLabel ? 28 : 0;
  const FRAME_GAP = 16; // gap between the two frames in double mode

  const totalH = isDouble
    ? FRAME_H * 2 + FRAME_GAP + LABEL_H
    : FRAME_H + LABEL_H;

  const viewBox = `0 0 ${FRAME_W} ${totalH}`;

  // Scale to provided width if given
  const svgWidth  = width ?? FRAME_W;
  const svgHeight = width ? Math.round((totalH / FRAME_W) * width) : totalH;

  const labelY = totalH - 6;
  const labelText = studentMode
    ? '___'
    : clampedValue === 0
      ? '0'
      : `${clampedValue}`;

  return (
    <svg
      viewBox={viewBox}
      width={svgWidth}
      height={svgHeight}
      className={className}
      role="img"
      aria-label={studentMode ? 'Empty ten frame for student completion' : `Ten frame showing ${clampedValue}`}
    >
      <title>{studentMode ? 'Ten frame (student)' : `Ten frame: ${clampedValue}`}</title>

      {/* Frame 1 border */}
      <rect
        x={PAD - 4} y={PAD - 4}
        width={FRAME_W - (PAD - 4) * 2}
        height={FRAME_H - (PAD - 4) * 2}
        rx={8} ry={8}
        fill="none"
        stroke={FRAME_STROKE}
        strokeWidth={2}
      />

      {/* Frame 1 — top row */}
      <TenFrameRow rowIndex={0} filledCount={topFill} offsetY={PAD} studentMode={studentMode} />
      {/* Frame 1 — bottom row */}
      <TenFrameRow rowIndex={1} filledCount={bottomFill} offsetY={PAD + CELL + GAP} studentMode={studentMode} />

      {/* Frame 2 (double ten-frame only) */}
      {isDouble && (
        <>
          <rect
            x={PAD - 4} y={FRAME_H + FRAME_GAP + PAD - 4}
            width={FRAME_W - (PAD - 4) * 2}
            height={FRAME_H - (PAD - 4) * 2}
            rx={8} ry={8}
            fill="none"
            stroke={FRAME_STROKE}
            strokeWidth={2}
          />
          <TenFrameRow rowIndex={2} filledCount={topFill2} offsetY={FRAME_H + FRAME_GAP + PAD} studentMode={studentMode} />
          <TenFrameRow rowIndex={3} filledCount={bottomFill2} offsetY={FRAME_H + FRAME_GAP + PAD + CELL + GAP} studentMode={studentMode} />
        </>
      )}

      {/* Count label */}
      {showLabel && (
        <text
          x={FRAME_W / 2} y={labelY}
          textAnchor="middle"
          fontSize={18}
          fontFamily="Nunito, sans-serif"
          fontWeight={700}
          fill={LABEL_COLOR}
        >
          {labelText}
        </text>
      )}
    </svg>
  );
}

// ── Utility: parse a ten-frame value from imageHint text ──────────────────────
/**
 * Extracts the target count from an AI-generated imageHint string.
 * Examples: "ten-frame with 7 filled" → 7
 *           "ten frame showing 12 dots" → 12
 *           "empty ten-frame" → 0 (studentMode=true)
 */
export function parseTenFrameHint(hint: string): { value: number; studentMode: boolean } {
  const h = hint.toLowerCase();
  if (/empty|blank|student|fill in/.test(h)) return { value: 0, studentMode: true };
  const match = h.match(/(\d+)/);
  return { value: match ? parseInt(match[1], 10) : 0, studentMode: !match };
}

/**
 * Returns true if this imageHint should be rendered as a TenFrameSVG.
 */
export function isTenFrameHint(hint: string): boolean {
  return /ten.?frame/i.test(hint);
}
